import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  adminUpdateGallery,
  adminListGallery,
  adminUpscaleGallery,
  adminRemixGalleryAi,
  adminIntegrations,
  adminVideoMetadata,
  resolveMedia,
} from "@/lib/api";
import { videoThumbUrl } from "@/lib/video";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, EyeOff, Star, Sparkles, Loader2, RefreshCw, Download, Plane, Camera } from "lucide-react";
import ShotDetailsEditor from "@/components/admin/ShotDetailsEditor";
import BehindTheLensEditor from "@/components/admin/BehindTheLensEditor";

const CATEGORIES = [
  "Drone",
  "Roadworks",
  "Structures",
  "People",
  "Night Works",
  "Depots",
  "Plant",
  "Traffic Management",
  "Events",
  "General",
  "Video",
];
const SIZES = ["standard", "portrait_tall", "landscape_wide", "large_feature"];

export default function AdminGalleryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({});
  const [technical, setTechnical] = useState(null);
  const [saving, setSaving] = useState(false);
  const [upscaling, setUpscaling] = useState(false);
  const [remixing, setRemixing] = useState(false);
  const [integrations, setIntegrations] = useState({ upscale_configured: false });
  const [upscaleScale, setUpscaleScale] = useState(2);
  const [fetchedMedia, setFetchedMedia] = useState(null);
  const [fetchingMeta, setFetchingMeta] = useState(false);

  useEffect(() => {
    adminListGallery()
      .then((items) => {
        const found = items.find((i) => i.id === Number(id));
        if (!found) {
          toast.error("Item not found.");
          navigate("/admin/gallery");
          return;
        }
        setItem(found);
        setTechnical(found.technical || null);
        setForm({
          title: found.title || "",
          original_name: found.original_name || "",
          client: found.client || "",
          category: found.category || "Roadworks",
          size: found.size || "standard",
          description: found.description || "",
          alt_text: found.alt_text || "",
          status: found.status || "published",
          featured: !!found.featured,
          video: found.video || "",
          video_kind: found.video_kind || "mp4",
          shot_type: found.shot_type || "ground",
        });
      })
      .catch(() => toast.error("Couldn't load item."));
    adminIntegrations().then(setIntegrations).catch(() => {});
  }, [id, navigate]);

  if (!item) {
    return (
      <div className="px-6 lg:px-10 py-10 text-[#737373] text-xs tracking-[0.24em] uppercase">
        Loading…
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateGallery(item.id, { ...form, ...(fetchedMedia || {}), technical });
      toast.success("Saved.");
      navigate("/admin/gallery");
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const fetchVideoMeta = async () => {
    const url = (form.video || "").trim();
    if (!url) {
      toast.error("Add the video URL first.");
      return;
    }
    setFetchingMeta(true);
    try {
      const res = await adminVideoMetadata(url);
      setFetchedMedia({
        image: res.image || undefined,
        thumbnail: res.thumbnail || undefined,
        webp_image: res.webp_image || undefined,
        webp_thumbnail: res.webp_thumbnail || undefined,
      });
      setForm((f) => ({
        ...f,
        video_kind: res.video_kind || f.video_kind,
        title: f.title || res.title,
        description: f.description || res.description,
      }));
      toast.success("Fetched from video, click Save changes to apply.");
    } catch (e) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't fetch video details.");
    } finally {
      setFetchingMeta(false);
    }
  };

  const runUpscale = async () => {
    if (!integrations.upscale_configured) {
      toast.error("Upscaling unavailable, server missing EMERGENT_LLM_KEY.");
      return;
    }
    if (!window.confirm(`Upscale this image ${upscaleScale}×? This may take 20-60 seconds and uses your Emergent LLM Key credits.`)) return;
    setUpscaling(true);
    try {
      const updated = await adminUpscaleGallery(item.id, upscaleScale);
      setItem(updated);
      toast.success("Image upscaled to higher resolution.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upscale failed.");
    } finally {
      setUpscaling(false);
    }
  };

  const runRemix = async () => {
    setRemixing(true);
    try {
      const data = await adminRemixGalleryAi(item.id);
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        category: data.category || f.category,
        size: data.suggested_size || f.size,
        alt_text: data.alt_text || f.alt_text,
        description: data.description || f.description,
      }));
      toast.success("Remix ready, review and save to apply.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Remix failed.");
    } finally {
      setRemixing(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-5xl">
      <Link
        to="/admin/gallery"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white mb-6"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
        Edit · #{item.id}
      </div>
      <h1 className="font-display text-3xl tracking-tight">{item.title}</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div
            className="relative bg-[#0a0a0a] overflow-hidden border border-[#1a1a1a] flex items-center justify-center"
            style={{ minHeight: 320, maxHeight: "70vh" }}
          >
            <img
              src={
                fetchedMedia?.image
                  ? resolveMedia(fetchedMedia.image)
                  : item.image
                  ? resolveMedia(item.image)
                  : item.type === "video"
                  ? videoThumbUrl({ ...item, video: form.video || item.video })
                  : ""
              }
              alt={item.alt_text || item.title}
              className="block w-full h-auto max-h-[70vh] object-contain"
            />
          </div>
          <div className="text-[11px] text-[#525252] mt-2 break-all">
            {fetchedMedia?.image || item.image || (item.type === "video" ? "No poster stored, showing thumbnail from the video platform" : "")}
          </div>

          {item.type === "video" && (
            <button
              type="button"
              onClick={fetchVideoMeta}
              disabled={fetchingMeta || !(form.video || "").trim()}
              data-testid="edit-fetch-video-meta"
              className="hl-btn-secondary mt-4 w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {fetchingMeta ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {fetchingMeta ? "Fetching from video…" : "Fetch thumbnail & details from video"}
            </button>
          )}

          {/* Upscale block - photos only */}
          {item.type === "photo" && (
            <div
              data-testid="upscale-panel"
              className="mt-6 bg-[#0f0f0f] border border-[#1a1a1a] p-4"
            >
              <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#4A6D8C] mb-3">
                <Sparkles size={12} /> AI Upscaling
              </div>
              {item.upscaled && (
                <div className="text-[11px] text-[#658CAE] mb-3 inline-flex items-center gap-2">
                  <Sparkles size={12} /> This image has been upscaled.
                </div>
              )}
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-2">Scale factor</div>
              <div className="flex gap-2 mb-4">
                {[2, 4].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setUpscaleScale(s)}
                    data-testid={`upscale-scale-${s}`}
                    className={`hl-chip ${upscaleScale === s ? "active" : ""}`}
                  >
                    {s}×
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={runUpscale}
                disabled={!integrations.upscale_configured || upscaling}
                data-testid="upscale-button"
                className="hl-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {upscaling ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Upscaling… 20-60s
                  </>
                ) : (
                  <>
                    <Sparkles size={14} /> Upscale to ultra-HD
                  </>
                )}
              </button>
              {!integrations.upscale_configured && (
                <div className="text-[11px] text-[#A3A3A3] mt-3 leading-relaxed">
                  Upscaling needs the Emergent universal key. Ask your developer to add{" "}
                  <code className="text-[#658CAE]">EMERGENT_LLM_KEY</code> to the backend{" "}
                  <code className="text-[#658CAE]">.env</code> file.
                </div>
              )}
              {integrations.upscale_configured && (
                <div className="text-[11px] text-[#525252] mt-3 leading-relaxed">
                  Uses Gemini Nano Banana for AI image enhancement, then a sharp LANCZOS pass to the target resolution. Original image is preserved in storage; this updates the image shown on the site.
                </div>
              )}
            </div>
          )}

          {item.type === "photo" && (
            <div className="mt-6">
              <ShotDetailsEditor
                itemId={item.id}
                technical={technical}
                onChange={setTechnical}
                testidPrefix="edit-tech"
              />
              <BehindTheLensEditor
                itemId={item.id}
                technical={technical}
                onChange={setTechnical}
              />
            </div>
          )}
        </div>

        <div className="lg:col-span-7 flex flex-col gap-1">
          {item.type === "video" && (
            <>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-2">
                Video URL ({form.video_kind})
              </label>
              <input
                type="text"
                value={form.video}
                onChange={(e) => setForm((f) => ({ ...f, video: e.target.value }))}
                className="hl-input"
                data-testid="edit-video"
              />
            </>
          )}

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-2">
            Title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className="hl-input"
            data-testid="edit-title"
          />

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-4">
            Original photo name
          </label>
          <input
            type="text"
            value={form.original_name}
            onChange={(e) => setForm((f) => ({ ...f, original_name: e.target.value }))}
            className="hl-input"
            data-testid="edit-original-name"
            placeholder="e.g. cones at night"
          />

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-4">
            Client
          </label>
          <input
            type="text"
            value={form.client}
            onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))}
            className="hl-input"
            data-testid="edit-client"
          />

          <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-6 mb-2">
            Category
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm((f) => ({ ...f, category: c }))}
                data-testid={`cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`hl-chip ${form.category === c ? "active" : ""}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-6 mb-2">
            Shot type <span className="normal-case tracking-normal text-[#525252]">(AI-detected - correct if wrong)</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="shot-type-picker">
            {[
              { id: "aerial", label: "Aerial / Drone", Icon: Plane },
              { id: "ground", label: "Ground", Icon: Camera },
            ].map(({ id: st, label, Icon }) => (
              <button
                key={st}
                type="button"
                onClick={() => setForm((f) => ({ ...f, shot_type: st }))}
                data-testid={`shot-type-${st}`}
                className={`hl-chip inline-flex items-center gap-1.5 ${form.shot_type === st ? "active" : ""}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-6 mb-2">
            Tile size
          </div>
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm((f) => ({ ...f, size: s }))}
                className={`hl-chip ${form.size === s ? "active" : ""}`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-7 mb-2">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
              AI metadata
            </div>
            <button
              type="button"
              onClick={runRemix}
              disabled={remixing}
              data-testid="edit-remix"
              className="hl-btn-secondary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ padding: "8px 14px", fontSize: 11 }}
            >
              {remixing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {remixing ? "Remixing…" : "Remix · fresh wording"}
            </button>
          </div>

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
            Alt text
          </label>
          <input
            type="text"
            value={form.alt_text}
            onChange={(e) => setForm((f) => ({ ...f, alt_text: e.target.value }))}
            className="hl-input"
            data-testid="edit-alt"
          />

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-4">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={4}
            className="hl-input"
            data-testid="edit-description"
          />

          <div className="flex flex-wrap items-center gap-6 mt-8">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, status: f.status === "published" ? "draft" : "published" }))}
              className="text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white inline-flex items-center gap-2"
              data-testid="edit-toggle-publish"
            >
              {form.status === "published" ? <Eye size={14} /> : <EyeOff size={14} />}
              {form.status === "published" ? "Published" : "Draft"}
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, featured: !f.featured }))}
              className={`text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 ${form.featured ? "text-[#658CAE]" : "text-[#A3A3A3] hover:text-white"}`}
              data-testid="edit-toggle-featured"
            >
              <Star size={14} fill={form.featured ? "currentColor" : "none"} />
              Featured
            </button>
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="edit-save"
            className="hl-btn-primary mt-10 self-start inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
